import { produce, WritableDraft } from 'immer';

import { FeaturePatternTrussContainer, TrussEnvelope } from '@paragon/contract-library';

import { SelectableItemOfType } from '../selection/types';
import { findNextName } from '../utilities/findNextName';
import { TransformableType } from './types';

export interface RenamingOptions {
  prefix?: string;
  takenNames: string[];
}

export function duplicateTransformableObjects<T extends TransformableType>(
  type: T,
  targets: SelectableItemOfType<T>[],

  options?: {
    rename?: RenamingOptions | null;

    /** if provided, this map will be populated to map each duplicate object's guid to its original guid */
    guidMapping?: Map<string, string>;

    /** if set, truss envelopes will be allowed to retain their linked component designs */
    preserveLinkedComponents?: boolean;
  },
): SelectableItemOfType<T>[] {
  const takenNames = options?.rename?.takenNames?.slice() ?? [];

  return targets.map(
    produce((target) => {
      const oldGuid = target.guid;
      const newGuid = crypto.randomUUID();

      target.guid = newGuid;
      options?.guidMapping?.set(newGuid, oldGuid);

      if ('name' in target && options?.rename) {
        target.name = findNextName(takenNames, { prefix: options.rename.prefix });
        takenNames.push(target.name);
      }

      if (type === 'trussEnvelope' && options?.preserveLinkedComponents !== true) {
        (target as WritableDraft<TrussEnvelope>).componentDesignGuid = null; // TODO: typescript
      }

      // the duplicated container's feature areas must not share guids with the original's, or selecting or deleting one
      //   would also affect the other
      if (type === 'featurePatternContainer') {
        for (const area of (target as WritableDraft<FeaturePatternTrussContainer>).featureAreas) {
          area.guid = crypto.randomUUID();
        }
      }
    }),
  );
}
